import Keycloak, {
  KeycloakConfig,
  KeycloakInitOptions,
  KeycloakInstance,
  KeycloakTokenParsed
} from 'keycloak-js'
import { TAuthData, TRole } from '../definitions/auth'

type TParsedToken = KeycloakTokenParsed & {
  realm_access?: {
    roles?: string[]
  }
  resource_access?: Record<string, { roles?: string[] }>
  given_name?: string
  family_name?: string
  preferred_username?: string
  organization?: string
  org?: string
  institution?: string
}

const KEYCLOAK_CHECK_SSO_PATH = '/silent-check-sso.html'
export const KEYCLOAK_REFRESH_INTERVAL_MS = 20000

const keycloakConfig: KeycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_URL || '',
  realm: process.env.REACT_APP_KEYCLOAK_REALM || '',
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || ''
}

const isConfigured = Boolean(keycloakConfig.url && keycloakConfig.realm && keycloakConfig.clientId)

let keycloak: KeycloakInstance | null = isConfigured ? new Keycloak(keycloakConfig) : null
let initPromise: Promise<boolean> | null = null

const getRemainingLifetime = (expiresAt?: number) => {
  if (!expiresAt) return 0
  const nowInSeconds = Math.floor(Date.now() / 1000)
  return Math.max(0, expiresAt - nowInSeconds)
}

const getName = (tokenParsed: TParsedToken) => {
  const fallbackName = (tokenParsed.name || '').trim().split(' ').filter(Boolean)
  return {
    firstName: tokenParsed.given_name || fallbackName[0] || '',
    lastName: tokenParsed.family_name || fallbackName.slice(1).join(' ') || ''
  }
}

const getAllRoles = (tokenParsed: TParsedToken, clientId: string): string[] => {
  const realmRoles = tokenParsed.realm_access?.roles || []
  const scopedClientRoles = tokenParsed.resource_access?.[clientId]?.roles || []
  const allClientRoles = Object.keys(tokenParsed.resource_access || {}).reduce<string[]>(
    (acc, key) => [...acc, ...(tokenParsed.resource_access?.[key]?.roles || [])],
    []
  )
  const combinedRoles = [...realmRoles, ...scopedClientRoles, ...allClientRoles]
  return combinedRoles.filter((role, index) => combinedRoles.indexOf(role) === index)
}

const toUserRole = (roles: string[]): TRole => (
  roles.includes('admin') ? 'admin' : 'user'
)

export const isKeycloakConfigured = () => isConfigured

export const initKeycloak = async () => {
  if (!keycloak) return false
  if (!initPromise) {
    const options: KeycloakInitOptions = {
      onLoad: 'check-sso',
      checkLoginIframe: true,
      pkceMethod: 'S256',
      silentCheckSsoFallback: true
    }

    if (typeof window !== 'undefined') {
      options.silentCheckSsoRedirectUri = `${window.location.origin}${KEYCLOAK_CHECK_SSO_PATH}`
    }

    initPromise = keycloak.init(options)
  }

  return initPromise
}

export const mapKeycloakToAuthData = (): TAuthData | undefined => {
  if (!keycloak?.authenticated || !keycloak.token) return undefined

  const tokenParsed = (keycloak.tokenParsed || {}) as TParsedToken
  const refreshTokenParsed = (keycloak.refreshTokenParsed || {}) as TParsedToken
  const { firstName, lastName } = getName(tokenParsed)
  const roles = getAllRoles(tokenParsed, keycloakConfig.clientId || '')

  return {
    id: tokenParsed.sub || '',
    firstName,
    lastName,
    email: tokenParsed.email || tokenParsed.preferred_username || '',
    organization: tokenParsed.organization || tokenParsed.org || tokenParsed.institution || '',
    userRole: toUserRole(roles),
    access_token: keycloak.token,
    refresh_token: keycloak.refreshToken || '',
    expires_in: getRemainingLifetime(tokenParsed.exp),
    refresh_expires_in: getRemainingLifetime(refreshTokenParsed.exp),
    token_type: keycloak.tokenParsed?.typ || 'Bearer'
  }
}

export const refreshKeycloakToken = async (minValidity = 30): Promise<string | undefined> => {
  if (!keycloak?.authenticated) return undefined
  await keycloak.updateToken(minValidity)
  return keycloak.token || undefined
}

export const loginWithKeycloak = async (redirectUri?: string) => {
  if (!keycloak) return
  await keycloak.login(redirectUri ? { redirectUri } : undefined)
}

export const logoutFromKeycloak = (redirectUri?: string) => {
  if (!keycloak) {
    if (redirectUri && typeof window !== 'undefined') {
      window.location.replace(redirectUri)
    }
    return
  }

  const logoutUrl = keycloak.createLogoutUrl(redirectUri ? { redirectUri } : undefined)
  window.location.replace(logoutUrl)
}

export const clearKeycloakSession = () => {
  keycloak?.clearToken()
}

export const resolveRedirectUri = (path?: string) => {
  if (typeof window === 'undefined') return undefined
  if (!path) return window.location.href

  if (/^https?:\/\//i.test(path)) {
    const url = new URL(path)
    return url.origin === window.location.origin ? url.toString() : window.location.href
  }

  if (!path.startsWith('/') || path.startsWith('//')) {
    return window.location.href
  }

  return `${window.location.origin}${path}`
}

export const resetKeycloak = () => {
  keycloak = isConfigured ? new Keycloak(keycloakConfig) : null
  initPromise = null
}
